import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useClient } from '../../context/ClientContext';
import DashboardHeader from './DashboardHeader';
import Footer from './Footer';
import './Dashboard.css';
import './EmailForm.css';

interface CsvUploadResult {
  message: string;
  total_rows: number;
  emails_sent: number;
  invalid_emails: number;
  invalid_email_list?: string[];
  failed_emails?: number;
  failed_email_list?: string[];
}

const CsvEmailUploadPage: React.FC = () => {
  const navigate = useNavigate();
  const { selectedClientId } = useClient();
  const [subject, setSubject] = useState('Your Backyard Renovation.');
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<CsvUploadResult | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setResult(null);

    if (!file) {
      setError('Please select a CSV file to upload.');
      return;
    }

    if (!subject.trim()) {
      setError('Subject is required.');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('csv', file);
      formData.append('subject', subject.trim());
      if (selectedClientId != null) {
        formData.append('client_id', String(selectedClientId));
      }

      const response = await api.post<CsvUploadResult>('/email/send-customer-email-csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
      });
      setResult(response.data);
      setFile(null);
      // Reset file input
      const input = document.getElementById('csv_file') as HTMLInputElement | null;
      if (input) input.value = '';
    } catch (err: any) {
      setError(
        err.response?.data?.message
        || err.response?.data?.errors?.csv?.[0]
        || err.response?.data?.errors?.subject?.[0]
        || 'Failed to process CSV. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dashboard">
      <DashboardHeader title="Upload CSV" showNav={false} showEmailButton={false} />
      <div className="dashboard-content">
        <div className="email-section">
          <div className="email-form-container">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0 }}>Upload CSV</h3>
              <button type="button" className="btn btn-secondary" onClick={() => navigate('/dashboard/email')}>
                ← Back to Send Email
              </button>
            </div>

            <p style={{ color: '#555', marginBottom: '20px', lineHeight: 1.5 }}>
              Upload a CSV with columns <strong>First Name</strong>, <strong>Last Name</strong>, and <strong>Email</strong>.
              Each valid row will receive a review invite email using the subject below.
            </p>

            <form onSubmit={handleSubmit} className="email-form">
              {error && <div className="alert alert-error">{error}</div>}

              {result && (
                <div className="alert alert-success" style={{ whiteSpace: 'pre-wrap' }}>
                  <strong>Results</strong>
                  <ul style={{ margin: '10px 0 0', paddingLeft: '20px' }}>
                    <li>Total rows processed: {result.total_rows}</li>
                    <li>Emails sent: {result.emails_sent}</li>
                    <li>Invalid email addresses: {result.invalid_emails}</li>
                    {(result.failed_emails ?? 0) > 0 && (
                      <li>Failed to send: {result.failed_emails}</li>
                    )}
                  </ul>
                  {result.invalid_email_list && result.invalid_email_list.length > 0 && (
                    <div style={{ marginTop: '10px' }}>
                      <strong>Invalid emails:</strong>
                      <ul style={{ margin: '6px 0 0', paddingLeft: '20px' }}>
                        {result.invalid_email_list.map((email, i) => (
                          <li key={`${email}-${i}`}>{email}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {(result.failed_email_list?.length ?? 0) > 0 && (
                    <div style={{ marginTop: '10px' }}>
                      <strong>Failed emails:</strong>
                      <ul style={{ margin: '6px 0 0', paddingLeft: '20px' }}>
                        {result.failed_email_list!.map((email, i) => (
                          <li key={`${email}-${i}`}>{email}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div style={{ marginTop: '12px' }}>
                    <Link to="/dashboard/sent-review-emails">View Sent Emails →</Link>
                  </div>
                </div>
              )}

              <div className="form-group">
                <label htmlFor="subject">Subject</label>
                <input
                  type="text"
                  id="subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  required
                  placeholder="Email subject"
                  maxLength={255}
                />
              </div>

              <div className="form-group">
                <label htmlFor="csv_file">CSV File</label>
                <input
                  type="file"
                  id="csv_file"
                  accept=".csv,text/csv"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  required
                />
                <small style={{ display: 'block', marginTop: '6px', color: '#666' }}>
                  Required headers: First Name, Last Name, Email
                </small>
              </div>

              <button type="submit" disabled={loading} className="btn btn-primary btn-block">
                {loading ? 'Uploading & Sending...' : 'Submit CSV'}
              </button>
            </form>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default CsvEmailUploadPage;
